import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormHelperText,
} from "@mui/material";
import UploadBox from "../fileUploader/UploadBox";
import UploaderLoadingComponent from "./UploaderLoadingComponent";
import { useDispatch, useSelector } from "react-redux";
import {
  handleLoadDocument,
  updateSelectedPackageFormObject,
  updateSelectedPackageObject,
  uploadUserPackagePDF,
} from "../../store/actions/userPackagesActions";
import RenderedPackageFormFields from "./RenderedPackageFormFields";

const steps = ["Upload Document", "Document Details"];

const AddingPackageStepper = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  setAddingNewPackage,
  uploadError,
  setUploadError,
  setPackagePanelOpen,
  setCurrentFormsActive,
  setSavedPackagesActive,
}) => {
  const { selectedPackage, selectedPackageForm } = useSelector(
    (state) => state.userPackages
  );

  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [packageName, setPackageName] = useState("");
  const [uploaderLoading, setUploaderLoading] = useState(false);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      setPackagePanelOpen(false);
      setAddingNewPackage(false);
    }

    setActiveStep((prevStep) => prevStep + 1);
  };
  const handleBack = () => {
    if (activeStep === 0) {
      setAddingNewPackage(false);
    } else {
      return;
    }
  };

  const handleExtractPDF = async () => {
    const response = await dispatch(
      uploadUserPackagePDF(
        currentProcessingFile,
        setUploaderLoading,
        packageName,
        null // ✅ packageID <-- New Package
      )
    );

    if (response) {
      const packageCopy = { ...response, documents: [response?.document] };
      delete packageCopy?.document;

      setCurrentProcessingFile(response);
      dispatch(updateSelectedPackageObject(packageCopy));
      dispatch(updateSelectedPackageFormObject(response?.document));

      await dispatch(
        handleLoadDocument(setUploaderLoading, setCurrentProcessingFile)
      );
      setCurrentFormsActive(true);
      setSavedPackagesActive(false);
      setAddingNewPackage(false);
      setActiveStep(0);
    }
  };

  useEffect(() => {
    if (uploaderLoading) {
      handleExtractPDF();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploaderLoading]);

  return (
    <>
      {/* Fixed Header with Stepper */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "white",
          p: 2,
          borderBottom: "3px solid #e0e0e0",
        }}
      >
        <Typography mb={1} align="center" variant="h4" color="primary">
          Create New Package
        </Typography>

        {/* MUI Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 2 }}>
        {activeStep === 0 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {uploaderLoading ? (
                <Typography variant="h6" color="primary">
                  {!packageName ? "Unnamed Package" : packageName}
                </Typography>
              ) : (
                <TextField
                  disabled={uploaderLoading}
                  value={packageName}
                  label="Package Name"
                  onChange={(e) => {
                    setPackageName(e.target.value);
                  }}
                />
              )}
            </Box>
            <FormHelperText sx={{ textAlign: "center" }}>
              {uploaderLoading
                ? "Your Package Name"
                : "Enter Your Package Name"}
            </FormHelperText>

            <Box mt={2}>
              {uploaderLoading ? (
                <UploaderLoadingComponent />
              ) : (
                <UploadBox
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                  setUploaderLoading={setUploaderLoading}
                />
              )}
            </Box>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Typography variant="h5" color="primary" align="center">
              Signers Information
            </Typography>
            <Typography mb={2} variant="h6" color="primary" align="center">
              You’ll be able to make changes later if required
            </Typography>
            <RenderedPackageFormFields
              selectedPackage={selectedPackage}
              selectedPackageForm={selectedPackageForm}
              firstLoad={true}
            />
          </>
        )}
      </Box>

      {/* Fixed Back-Next Buttons at the Bottom */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          bgcolor: "white",
          p: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          disabled={uploaderLoading}
          variant="contained"
          color={activeStep === 0 ? "error" : "secondary"}
          onClick={handleBack}
          sx={{ visibility: activeStep > 0 && "hidden" }}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>
        <Button
          variant="contained"
          color={activeStep === steps.length - 1 ? "success" : "primary"}
          onClick={handleNext}
          disabled={activeStep === 0 || uploaderLoading}
          sx={{
            visibility: activeStep === 0 && "hidden",
            width: activeStep === steps.length - 1 ? "15rem" : undefined,
          }}
        >
          {activeStep === steps.length - 1 ? "Load Document" : "Next"}
        </Button>
      </Box>
    </>
  );
};

export default AddingPackageStepper;
